import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { apiService } from '../services/api';
import { createLogger } from '../utils/logger';
import type { VerifyResponse } from '../services/api';

const logger = createLogger('AuthContext');

interface AuthContextType {
  isAuthenticated: boolean;
  isPro: boolean;
  subscriptionPlan: 'FREE' | 'PRO' | 'PREMIUM' | null;
  email: string | null;
  /** Подписка отменена, PRO до конца периода */
  cancelAtPeriodEnd: boolean;
  /** Дата окончания доступа (ISO), при cancelAtPeriodEnd */
  accessUntil: string | null;
  /** Сообщение при переключении с PRO на FREE из‑за 403 (сессия прервалась) */
  sessionDowngradedMessage: string | null;
  dismissSessionDowngraded: () => void;
  login: (email: string) => Promise<void>;
  loginWithGoogle: (idToken: string) => Promise<void>;
  logout: () => Promise<void>;
  verifyToken: (token: string, email: string) => Promise<void>;
  /** Обновить данные пользователя (после оплаты или возврата из Stripe Portal) */
  refreshUser: () => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [subscriptionPlan, setSubscriptionPlan] = useState<'FREE' | 'PRO' | 'PREMIUM' | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(false);
  const [accessUntil, setAccessUntil] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionDowngradedMessage, setSessionDowngradedMessage] = useState<string | null>(null);

  const applyUserData = useCallback((userData: VerifyResponse) => {
    const until = (userData.accessUntil ?? userData.access_until ?? null) || null;
    const plan = userData.subscriptionPlan;
    const cancelled = !!(userData.cancelAtPeriodEnd ?? userData.cancel_at_period_end);
    setIsAuthenticated(true);
    setSubscriptionPlan(plan);
    setEmail(userData.email ?? null);
    setCancelAtPeriodEnd(cancelled);
    setAccessUntil(until);
    if (userData.email) localStorage.setItem('email', userData.email);
    if (plan) localStorage.setItem('subscriptionPlan', plan);
  }, []);

  const clearAuthState = useCallback(() => {
    setIsAuthenticated(false);
    setSubscriptionPlan(null);
    setEmail(null);
    setCancelAtPeriodEnd(false);
    setAccessUntil(null);
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('email');
    localStorage.removeItem('subscriptionPlan');
  }, []);

  useEffect(() => {
    const onSessionDowngraded = () => {
      if (loading || !isAuthenticated) return;
      setSubscriptionPlan('FREE');
      setSessionDowngradedMessage('Session interrupted — you are now viewing free data. Log in again for Premium.');
    };
    window.addEventListener('session-downgraded', onSessionDowngraded);
    return () => window.removeEventListener('session-downgraded', onSessionDowngraded);
  }, [loading, isAuthenticated]);

  const dismissSessionDowngraded = () => setSessionDowngradedMessage(null);

  useEffect(() => {
    // Проверяем наличие токена при загрузке и вызываем /api/auth/me.
    // В React.StrictMode (dev) запрос может выполниться повторно, но состояние при этом остаётся корректным.
    let cancelled = false;
    const checkAuth = async () => {
      const token = localStorage.getItem('accessToken');
      const cachedPlan = localStorage.getItem('subscriptionPlan') as 'FREE' | 'PRO' | 'PREMIUM' | null;
      const cachedEmail = localStorage.getItem('email');
      
      if (token) {
        // Оптимистично показываем последний известный план, чтобы не мигать в FREE при временных сбоях.
        if (cachedPlan === 'PRO' || cachedPlan === 'PREMIUM' || cachedPlan === 'FREE') {
          setIsAuthenticated(true);
          setSubscriptionPlan(cachedPlan);
          setEmail(cachedEmail || null);
        }

        const loadMe = async (): Promise<boolean> => {
          const userData = await apiService.getMe();
          if (cancelled) return false;
          applyUserData(userData);
          return true;
        };

        try {
          await loadMe();
        } catch (error: any) {
          logger.warn('Initial /api/auth/me failed, trying forced refresh', { message: error?.message, status: error?.status });
          try {
            await apiService.refreshAccessToken();
            await loadMe();
          } catch (finalError: any) {
            const msg = String(finalError?.message || '').toLowerCase();
            const status = finalError?.status;
            const isAuthFailure =
              status === 401 ||
              msg.includes('authentication failed') ||
              msg.includes('login again') ||
              msg.includes('refresh token expired') ||
              msg.includes('unauthorized');

            if (cancelled) return;
            if (isAuthFailure) {
              logger.warn('Token validation failed with auth error, clearing session', { message: finalError?.message, status });
              clearAuthState();
            } else {
              // Не сбрасываем в FREE на сетевых/временных ошибках: сохраняем кэшированный план.
              logger.warn('Non-auth failure during auth check, keeping cached session', { message: finalError?.message, status });
              setIsAuthenticated(true);
              if (cachedPlan) setSubscriptionPlan(cachedPlan);
              if (cachedEmail) setEmail(cachedEmail);
            }
          }
        }
      } else {
        if (!cancelled) {
          clearAuthState();
        }
      }

      if (!cancelled) {
        setLoading(false);
      }
    };
    
    checkAuth();
    return () => {
      cancelled = true;
    };
  }, [applyUserData, clearAuthState]);

  const login = async (email: string) => {
    await apiService.login(email);
    // Magic link отправлен, пользователь получит email
  };

  const loginWithGoogle = async (idToken: string) => {
    const userData = await apiService.loginWithGoogle(idToken);
    applyUserData(userData);
    try {
      const freshUserData = await apiService.getMe();
      applyUserData(freshUserData);
    } catch (e) {
      logger.warn('Google login /api/auth/me refresh failed, keeping token response data', e);
    }
  };

  const verifyToken = async (token: string, email: string) => {
    await apiService.verifyToken(token, email);
    const userData = await apiService.getMe();
    applyUserData(userData);
  };

  const logout = async () => {
    await apiService.logout();
    setIsAuthenticated(false);
    setSubscriptionPlan(null);
    setEmail(null);
    setCancelAtPeriodEnd(false);
    setAccessUntil(null);
  };

  const refreshUser = useCallback(async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) return;
    try {
      const userData = await apiService.getMe();
      applyUserData(userData);
    } catch (e) {
      logger.warn('refreshUser failed', e);
    }
  }, [applyUserData]);

  const isPro = subscriptionPlan === 'PRO' || subscriptionPlan === 'PREMIUM';

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isPro,
        subscriptionPlan,
        email,
        cancelAtPeriodEnd,
        accessUntil,
        sessionDowngradedMessage,
        dismissSessionDowngraded,
        login,
        loginWithGoogle,
        logout,
        verifyToken,
        refreshUser,
        loading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
